import {
  buildOperationalStepReassignmentKey,
  formatTons,
  getClientSelectionId,
  getDeliveryHighlightKey,
  getLoadingHighlightKey,
  getLoadingOrderHighlightKey,
  getTripRouteBadgeMeta,
} from "./resultsFleetData.js";
import { getAssignmentTripCount } from "./resultsFleetCardShell.js";
import {
  normalizeClientDisplayLabel,
  resolveOrderProductCodeDisplay,
} from "./displayLabelFormatters.js";
import {
  PROTECTION_VARIANT_REORDERED,
  PROTECTION_VARIANT_STANDARD,
} from "./protectionVisualState.js";
import {
  isImplicitFrozenDispatcherStatusCode,
  resolveSharedLoadingFreezeChain,
} from "./dispatcherFreezeStatus.js";

export function normalizePendingMutationIds(value) {
  if (value && typeof value.has === "function") {
    return value;
  }

  return new Set(Array.isArray(value) ? value.filter((entry) => typeof entry === "string") : []);
}

const EMPTY_OPERATIONAL_STEP_SEQUENCE_LOOKUP = new Map();

function normalizeOperationalStepMatchValue(value) {
  return typeof value === "string" ? value.trim() : "";
}

function isOperationalRouteStop(stop) {
  return stop?.stop_type === "loading" || stop?.stop_type === "delivery";
}

function getTripRouteStops(trip) {
  if (Array.isArray(trip?.routable_stops) && trip.routable_stops.length) {
    return trip.routable_stops.filter(Boolean);
  }

  return Array.isArray(trip?.stops) ? trip.stops.filter(Boolean) : [];
}

function getOperationalStepSequenceLookupKeys(step) {
  const stepType = normalizeOperationalStepMatchValue(step?.step_type);
  const stepId = normalizeOperationalStepMatchValue(step?.step_id);
  const deliveryId = normalizeOperationalStepMatchValue(step?.delivery_id);
  const orderId = normalizeOperationalStepMatchValue(step?.order_id);
  const keys = [];

  if (stepType === "delivery") {
    if (deliveryId) {
      keys.push(`delivery:${deliveryId}`);
    }

    if (orderId) {
      keys.push(`delivery-order:${orderId}`);
    }

    return keys;
  }

  if (stepType === "loading") {
    if (stepId) {
      keys.push(`loading-step:${stepId}`);
    }

    if (deliveryId) {
      keys.push(`loading-delivery:${deliveryId}`);
    }

    if (orderId) {
      keys.push(`loading-order:${orderId}`);
    }
  }

  return keys;
}

function doesOperationalStepMatchRouteStop(step, stop) {
  const stepType = normalizeOperationalStepMatchValue(step?.step_type);
  const stopType = normalizeOperationalStepMatchValue(stop?.stop_type);
  if (!stepType || stepType !== stopType) {
    return false;
  }

  const stepDeliveryId = normalizeOperationalStepMatchValue(step?.delivery_id);
  const stopDeliveryId = normalizeOperationalStepMatchValue(stop?.delivery_id);
  const stepOrderId = normalizeOperationalStepMatchValue(step?.order_id);
  const stopOrderId = normalizeOperationalStepMatchValue(stop?.order_id);

  if (stepType === "delivery") {
    if (stepDeliveryId || stopDeliveryId) {
      return Boolean(stepDeliveryId) && stepDeliveryId === stopDeliveryId;
    }

    return Boolean(stepOrderId) && stepOrderId === stopOrderId;
  }

  if (stepOrderId && stopOrderId) {
    return stepOrderId === stopOrderId;
  }

  const stepId = normalizeOperationalStepMatchValue(step?.step_id);
  const stopStepId = normalizeOperationalStepMatchValue(stop?.step_id);
  if (stepId && stopStepId) {
    return stepId === stopStepId;
  }

  return Boolean(stepDeliveryId) && stepDeliveryId === stopDeliveryId;
}

function buildOperationalStepDisplaySequenceLookup(trip, tripRecords = []) {
  const stops = getTripRouteStops(trip);
  const unresolvedSteps = Array.isArray(tripRecords)
    ? tripRecords.map((record) => record?.step).filter(Boolean)
    : [];
  if (stops.length < 2 || !unresolvedSteps.length) {
    return EMPTY_OPERATIONAL_STEP_SEQUENCE_LOOKUP;
  }

  const sequenceOffset = stops[0]?.stop_type === "depot_start" && isOperationalRouteStop(stops[1])
    ? 1
    : 0;
  const displaySequenceLookup = new Map();

  stops.slice(0, -1).forEach((_, stopIndex) => {
    const targetStop = stops[stopIndex + 1];
    if (!isOperationalRouteStop(targetStop)) {
      return;
    }

    const matchedStepIndex = unresolvedSteps.findIndex((step) => doesOperationalStepMatchRouteStop(step, targetStop));
    if (matchedStepIndex < 0) {
      return;
    }

    const [matchedStep] = unresolvedSteps.splice(matchedStepIndex, 1);
    const displaySequence = stopIndex + 1 + sequenceOffset;
    getOperationalStepSequenceLookupKeys(matchedStep).forEach((key) => {
      if (!displaySequenceLookup.has(key)) {
        displaySequenceLookup.set(key, displaySequence);
      }
    });
  });

  return displaySequenceLookup.size ? displaySequenceLookup : EMPTY_OPERATIONAL_STEP_SEQUENCE_LOOKUP;
}

function resolveOperationalStepDisplaySequence(step, displaySequenceByStepKey = EMPTY_OPERATIONAL_STEP_SEQUENCE_LOOKUP) {
  for (const key of getOperationalStepSequenceLookupKeys(step)) {
    const displaySequence = displaySequenceByStepKey.get(key);
    if (Number.isFinite(displaySequence) && displaySequence > 0) {
      return displaySequence;
    }
  }

  const fallbackSequence = Number(step?.sequence);
  return Number.isFinite(fallbackSequence) && fallbackSequence > 0 ? fallbackSequence : null;
}

function formatOperationalStepSequenceLabel(value) {
  const numericValue = Number(value);
  return `Étape ${Number.isFinite(numericValue) && numericValue > 0 ? numericValue : "-"}`;
}

function formatOperationalStepCompartments(step) {
  const allocations = Array.isArray(step?.compartment_allocations) ? step.compartment_allocations : [];
  if (!allocations.length) {
    return "";
  }

  return allocations
    .map((allocation) => `C${allocation.position} ${formatTons(allocation.quantity_tons)} t`)
    .join(", ");
}

function normalizeOperationalStepIdList(value) {
  return Array.isArray(value)
    ? value.map((entry) => normalizeOperationalStepMatchValue(entry)).filter(Boolean)
    : [];
}

function findOperationalStepCompanion(step, tripRecords) {
  const candidateSteps = Array.isArray(tripRecords)
    ? tripRecords.map((record) => record?.step).filter(Boolean)
    : [];

  if (step?.step_type === "loading") {
    return candidateSteps.find((candidate) => (
      candidate?.step_type === "delivery"
      && (
        (candidate?.loading_step_id && candidate.loading_step_id === step.step_id)
        || normalizeOperationalStepIdList(candidate?.loading_step_ids).includes(normalizeOperationalStepMatchValue(step?.step_id))
        || (candidate?.order_id && candidate.order_id === step.order_id)
      )
    )) || null;
  }

  const groupedLoadingStepIds = normalizeOperationalStepIdList(step?.loading_step_ids);
  return candidateSteps.find((candidate) => (
    candidate?.step_type === "loading"
    && (
      (step?.loading_step_id && candidate.step_id === step.loading_step_id)
      || groupedLoadingStepIds.includes(normalizeOperationalStepMatchValue(candidate?.step_id))
      || (candidate?.order_id && candidate.order_id === step?.order_id)
    )
  )) || null;
}

function formatDeliveryWindow(order, delivery) {
  const deliveryRawData = delivery?.raw_data && typeof delivery.raw_data === "object" ? delivery.raw_data : {};
  const deliveryWindow = typeof deliveryRawData.Plage_horaire === "string" ? deliveryRawData.Plage_horaire.trim() : "";
  const plannedTime = typeof delivery?.planned_time === "string" ? delivery.planned_time.trim() : "";

  if (deliveryWindow) {
    return deliveryWindow;
  }

  if (plannedTime) {
    return plannedTime;
  }

  return null;
}

function buildOperationalStepOrderDetails(step, trip) {
  const orders = Array.isArray(trip?.orders) ? trip.orders.filter(Boolean) : [];
  const order = orders.find((candidate) => (
    (candidate?.order_id && candidate.order_id === step?.order_id)
    || (Array.isArray(candidate?.deliveries) && candidate.deliveries.some((delivery) => delivery?.delivery_id === step?.delivery_id))
  ));

  if (!order) {
    return null;
  }

  const deliveries = Array.isArray(order.deliveries) ? order.deliveries.filter(Boolean) : [];
  const primaryDelivery = deliveries.find((delivery) => delivery?.delivery_id === step?.delivery_id) || deliveries[0] || null;
  const quantityTons = Number.isFinite(order.quantity_tons)
    ? order.quantity_tons
    : (Number.isFinite(primaryDelivery?.quantity_tons) ? primaryDelivery.quantity_tons : step?.quantity_tons);

  return {
    deliveryAddress: primaryDelivery?.address || order.address || step?.address || "",
    deliveryClientName: normalizeClientDisplayLabel(primaryDelivery?.client_name || order.client_name || step?.client_name) || "",
    deliveryWindow: formatDeliveryWindow(order, primaryDelivery),
    editableOrder: {
      ...order,
      deliveries,
    },
    grainCode: resolveOrderProductCodeDisplay(order),
    grainType: order.grain_type || step?.grain_type || "",
    loadingAddress: order.loading_address || "",
    loadingClientName: normalizeClientDisplayLabel(order.loading_client_name) || "",
    orderId: order.order_id || step?.order_id || "",
    workOrderNumber: order.work_order_number || step?.work_order_number || "",
    planningNote: order.planning_note || "",
    quantityLabel: Number.isFinite(quantityTons) ? `${formatTons(quantityTons)} t` : "",
  };
}

function resolveManualStatusDeliveryId(step, reassignmentContexts, suspendDeliveryId) {
  if (typeof step?.delivery_id === "string" && step.delivery_id) {
    return step.delivery_id;
  }

  if (suspendDeliveryId) {
    return suspendDeliveryId;
  }

  const firstContext = Array.isArray(reassignmentContexts) ? reassignmentContexts[0] : null;
  return typeof firstContext?.deliveryId === "string" ? firstContext.deliveryId : "";
}

function isPendingApprovalBlockingReason(reason) {
  if (typeof reason !== "string") {
    return false;
  }

  const normalizedReason = reason.trim().toLowerCase();
  return normalizedReason.includes("attente d'approbation")
    || normalizedReason.includes("a approuver")
    || normalizedReason.includes("non approuv");
}

function isOperationalStepExplicitlyConfirmed(step) {
  if (!step || typeof step !== "object") {
    return false;
  }

  const manualStatusCode = typeof step.manual_status_code === "string" ? step.manual_status_code : "";
  return Boolean(
    step.is_confirmed
    || manualStatusCode === "delivery_confirmed"
    || manualStatusCode === "loading_confirmed",
  );
}

function isLoadingStepOrderProtected(step, tripRecords) {
  if (step?.step_type !== "loading") {
    return false;
  }

  if (isOperationalStepExplicitlyConfirmed(step)) {
    return true;
  }

  const companionStep = findOperationalStepCompanion(step, tripRecords);
  return isOperationalStepExplicitlyConfirmed(companionStep);
}

function buildOperationalStepManualStatus(step, reassignmentContexts, suspendDeliveryId, tripRecords = []) {
  const manualStatusCode = typeof step?.manual_status_code === "string" ? step.manual_status_code : "";
  const hasManualReassignment = manualStatusCode === "delivery_reassigned"
    || (Array.isArray(reassignmentContexts)
    ? reassignmentContexts.some((context) => context?.isManual)
    : false);
  const manualReorderReleaseDeliveryId = typeof step?.manual_reorder_release_delivery_id === "string"
    ? step.manual_reorder_release_delivery_id
    : "";
  const manualReorderScope = typeof step?.manual_reorder_scope === "string"
    ? step.manual_reorder_scope
    : "";
  const manualReorderDeliveryId = manualReorderReleaseDeliveryId || resolveManualStatusDeliveryId(step, reassignmentContexts, suspendDeliveryId);
  const isManuallyReordered = Boolean(step?.is_manually_reordered) && Boolean(manualReorderDeliveryId);

  if (manualStatusCode === "delivery_suspended") {
    return {
      code: "suspended",
      description: "Commande retirée de la flotte active. Elle restera hors planning tant qu'une réactivation explicite n'est pas demandée.",
      icon: "pause",
      label: "Suspendue",
      tone: "warning",
    };
  }

  if (isManuallyReordered) {
    return {
      code: "reordered",
      deliveryId: manualReorderDeliveryId,
      description: manualReorderScope === "deliveries-with-same-parent-load"
        ? "Livraison réordonnée manuellement dans ce chargement. Utilisez Libérer pour la rendre à nouveau optimisable."
        : "Commande réordonnée manuellement sur ce camion. Utilisez Libérer pour la rendre à nouveau optimisable.",
      icon: "shield",
      label: "Réordonnée",
      protectionVariant: PROTECTION_VARIANT_REORDERED,
      tone: "manual",
    };
  }

  if (
    manualStatusCode === "delivery_confirmed"
    || manualStatusCode === "loading_confirmed"
    || isLoadingStepOrderProtected(step, tripRecords)
  ) {
    return {
      code: "protected",
      description: "Commande protégée. Le bouton Optimiser ne la rebougera pas tant que la protection reste active.",
      icon: "shield",
      label: "Protégée",
      protectionVariant: PROTECTION_VARIANT_STANDARD,
      tone: "success",
    };
  }

  if (hasManualReassignment) {
    return {
      code: "reassigned",
      deliveryId: resolveManualStatusDeliveryId(step, reassignmentContexts, suspendDeliveryId),
      description: "Commande assignée manuellement à un camion. Elle peut encore être déplacée vers un autre camion compatible ou libérée pour redevenir optimisable.",
      icon: "shuffle",
      label: "Réaffectée",
      tone: "manual",
    };
  }

  return null;
}

function normalizeDispatcherExecutionStatus(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function normalizePortalConfirmationStatus(value) {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (normalized === "confirmée" || normalized === "confirmee") {
    return "confirmée";
  }
  if (normalized === "à_revalider" || normalized === "a_revalider") {
    return "à_revalider";
  }
  if (normalized === "non_confirmée" || normalized === "non_confirmee") {
    return "non_confirmée";
  }
  return normalized;
}

export function resolvePortalDriverConfirmationHighlight({
  confirmationPortail = "",
  gelOrigine = "",
  dispatcherStatusCode = "",
} = {}) {
  const normalizedConfirmation = normalizePortalConfirmationStatus(confirmationPortail);
  if (normalizedConfirmation === "confirmée") {
    return true;
  }

  const normalizedStatusCode = typeof dispatcherStatusCode === "string"
    ? dispatcherStatusCode.trim().toLowerCase()
    : "";
  const normalizedGelOrigine = typeof gelOrigine === "string" ? gelOrigine.trim().toLowerCase() : "";

  // Gel explicite portail: la commande a bien été confirmée par le chauffeur.
  if (normalizedStatusCode === "frozen" && normalizedGelOrigine === "portail") {
    return true;
  }

  // Gel de préfixe: gel_origine peut être hérité d'une commande aval confirmée.
  return false;
}

function normalizeDispatcherFrozenKind(value) {
  const normalized = typeof value === "string" ? value.trim().toLowerCase() : "";
  return normalized === "prefix" || normalized === "shared_loading" ? normalized : "";
}

function resolveDispatcherFrozenKind(step, relatedDeliveryItem = null) {
  return normalizeDispatcherFrozenKind(step?.dispatcher_frozen_kind)
    || normalizeDispatcherFrozenKind(relatedDeliveryItem?.dispatcher_frozen_kind);
}

function buildOperationalStepDispatcherStatus(step, relatedDeliveryItem = null) {
  const executionStatus = normalizeDispatcherExecutionStatus(step?.dispatcher_execution_status)
    || normalizeDispatcherExecutionStatus(relatedDeliveryItem?.dispatcher_execution_status);
  const isDispatcherFrozen = Boolean(step?.is_dispatcher_frozen || relatedDeliveryItem?.is_dispatcher_frozen);
  const dispatcherFrozenKind = resolveDispatcherFrozenKind(step, relatedDeliveryItem);
  const gelOrigine = (typeof step?.gel_origine === "string" && step.gel_origine.trim().toLowerCase())
    || (typeof relatedDeliveryItem?.gel_origine === "string" && relatedDeliveryItem.gel_origine.trim().toLowerCase())
    || "";
  const confirmationPortail = (typeof step?.confirmation_portail === "string" && step.confirmation_portail.trim().toLowerCase())
    || (typeof relatedDeliveryItem?.confirmation_portail === "string" && relatedDeliveryItem.confirmation_portail.trim().toLowerCase())
    || "";
  const statusLabel = typeof step?.dispatcher_execution_status_label === "string" && step.dispatcher_execution_status_label.trim()
    ? step.dispatcher_execution_status_label.trim()
    : (typeof relatedDeliveryItem?.dispatcher_execution_status_label === "string" && relatedDeliveryItem.dispatcher_execution_status_label.trim()
      ? relatedDeliveryItem.dispatcher_execution_status_label.trim()
      : "");

  if (executionStatus === "frozen") {
    const isPortal = resolvePortalDriverConfirmationHighlight({
      confirmationPortail,
      gelOrigine,
      dispatcherStatusCode: "frozen",
    });
    return {
      code: "frozen",
      gelOrigine: isPortal ? "portail" : "manuel",
      description: isPortal
        ? "Commande gelée suite à une confirmation du portail chauffeur. Le dégel fera passer la confirmation à « à revalider »."
        : "Commande gelée : « Optimiser » ne la déplace pas tant qu'elle reste gelée.",
      icon: isPortal ? "smartphone" : "pause",
      label: statusLabel || (isPortal ? "Gelée (portail)" : "Gelée"),
      tone: isPortal ? "info" : "warning",
    };
  }

  if (executionStatus === "delivered") {
    return {
      code: "delivered",
      description: "Commande livrée. Elle ne sera plus déplacée.",
      icon: "check",
      label: statusLabel || "Livrée",
      tone: "success",
    };
  }

  if (isDispatcherFrozen) {
    if (dispatcherFrozenKind === "shared_loading") {
      return {
        code: "frozen_shared_loading",
        gelOrigine: gelOrigine || "",
        description: "Commande implicitement gelée parce que son chargement est encore dans le camion après une livraison gelée du même lot.",
        icon: "lock",
        label: statusLabel || "Gelée",
        tone: "warning",
      };
    }

    const isPortal = resolvePortalDriverConfirmationHighlight({
      confirmationPortail,
      gelOrigine,
      dispatcherStatusCode: "frozen_prefix",
    });
    return {
      code: "frozen_prefix",
      gelOrigine: isPortal ? "portail" : "manuel",
      description: isPortal
        ? "Commande implicitement gelée car elle précède une commande gelée par le portail chauffeur."
        : "Commande implicitement gelée car elle précède une commande gelée dans cette tournée. Elle reste exclue des réoptimisations locales et flotte tant que le gel explicite en aval n'est pas retiré.",
      icon: isPortal ? "smartphone" : "pause",
      label: statusLabel || "Gelée",
      tone: isPortal ? "info" : "warning",
    };
  }

  return null;
}

function buildGanttDispatcherActionButton({
  canManageDispatcherFreeze = false,
  dispatcherActionButton = null,
  dispatcherStatus = null,
  freezeChain = null,
  isLoadingStep = false,
  pendingDeliveryIds,
}) {
  if (canManageDispatcherFreeze !== true || isLoadingStep) {
    return null;
  }

  const isChainMember = freezeChain?.isMember === true;
  const isTail = freezeChain?.isTail === true;
  const anchorDeliveryIds = Array.isArray(freezeChain?.anchorDeliveryIds)
    ? [...new Set(freezeChain.anchorDeliveryIds.filter((id) => typeof id === "string" && id.trim()))]
    : [];
  const groupStepIds = Array.isArray(freezeChain?.anchorStepIds)
    ? [...new Set(freezeChain.anchorStepIds.filter((id) => typeof id === "string" && id.trim()))]
    : [];

  if (isChainMember) {
    if (isTail && anchorDeliveryIds.length > 0) {
      const isPortal = String(dispatcherStatus?.gelOrigine || "").trim().toLowerCase() === "portail";
      const isGroup = anchorDeliveryIds.length >= 2;
      return {
        ariaLabel: isPortal
          ? (isGroup
            ? "Dégeler les ancres de ce lot — les confirmations portail passeront à « à revalider »"
            : "Dégeler cette commande — la confirmation portail passera à « à revalider »")
          : (isGroup
            ? "Dégeler les ancres explicites de ce lot"
            : "Dégeler cette commande"),
        deliveryId: anchorDeliveryIds[0],
        deliveryIds: anchorDeliveryIds,
        disabled: anchorDeliveryIds.some((id) => pendingDeliveryIds.has(id)),
        gelOrigine: isPortal ? "portail" : "manuel",
        groupStepIds,
        icon: "rotate-ccw",
        label: isPortal ? "Dégeler (portail)" : "Dégeler",
        mode: "unfreeze",
        requiresPortalUnfreezeConfirmation: isPortal,
        title: isPortal ? "Dégeler — confirmation portail" : "Dégeler",
        unfreezeMode: isGroup ? "group" : "single",
      };
    }
    return null;
  }

  return dispatcherActionButton;
}

function buildOperationalStepTimeWindowOverride(step, relatedDeliveryItem = null) {
  const timeWindowOverride = step?.time_window_override && typeof step.time_window_override === "object"
    ? step.time_window_override
    : relatedDeliveryItem?.time_window_override && typeof relatedDeliveryItem.time_window_override === "object"
      ? relatedDeliveryItem.time_window_override
      : null;
  if (!timeWindowOverride) {
    return null;
  }

  const mode = typeof timeWindowOverride.mode === "string" ? timeWindowOverride.mode.trim().toLowerCase() : "";
  if (!mode) {
    return null;
  }

  const detail = typeof timeWindowOverride.detail === "string"
    ? timeWindowOverride.detail.trim()
    : "";
  const reason = typeof timeWindowOverride.reason === "string"
    ? timeWindowOverride.reason.trim()
    : "";
  const reasonLabel = typeof timeWindowOverride.reason_label === "string"
    ? timeWindowOverride.reason_label.trim()
    : "";
  const recordedAt = typeof timeWindowOverride.recorded_at === "string"
    ? timeWindowOverride.recorded_at.trim()
    : "";
  const isOverrideRequired = mode === "override_required";

  return {
    code: isOverrideRequired ? "override_required" : "soft",
    confirmed: timeWindowOverride.confirmed === true,
    description: detail
      || reasonLabel
      || (isOverrideRequired
        ? "Un motif manuel a été enregistré pour autoriser une sortie de fenêtre horaire."
        : "Une confirmation manuelle de fenêtre horaire a été enregistrée."),
    detail,
    label: isOverrideRequired ? "Override horaire" : "Fenêtre confirmée",
    modeLabel: isOverrideRequired ? "Motif obligatoire" : "Confirmation simple",
    reason,
    reasonLabel,
    recordedAt,
  };
}

export function buildOperationalStepViewModel({
  assignmentId,
  canManageDispatcherFreeze = false,
  displaySequenceByStepKey = EMPTY_OPERATIONAL_STEP_SEQUENCE_LOOKUP,
  openOperationalReassignmentKey,
  pendingDeliveryIds,
  pendingLoadingIds,
  selectedClientId,
  stepRecord,
  transientActivatedStepKeys,
  trip,
  tripRecords,
}) {
  const step = stepRecord?.step && typeof stepRecord.step === "object" ? stepRecord.step : {};
  const reassignmentContexts = Array.isArray(stepRecord?.reassignmentContexts) ? stepRecord.reassignmentContexts : [];
  const clientId = getClientSelectionId(step);
  const isLoadingStep = step.step_type === "loading";
  const hasReassignmentContexts = reassignmentContexts.length > 0;
  const allReassignmentsPending = hasReassignmentContexts && reassignmentContexts.every((context) => context.isPending);
  const reassignmentTitle = step.step_type === "loading"
    ? reassignmentContexts.length > 1
      ? "Afficher les camions compatibles pour les fermes de ce chargement"
      : "Afficher les camions compatibles pour cette ferme"
    : "Afficher les camions compatibles pour cette livraison";
  const summaryLabel = formatOperationalStepCompartments(step);
  const loadingStepId = typeof step.step_id === "string" ? step.step_id : "";
  const deliveryId = typeof step.delivery_id === "string" ? step.delivery_id : "";
  const suspendDeliveryId = typeof step.suspend_delivery_id === "string" ? step.suspend_delivery_id : "";
  const relatedDeliveryItem = stepRecord?.relatedDeliveryItem && typeof stepRecord.relatedDeliveryItem === "object"
    ? stepRecord.relatedDeliveryItem
    : null;
  const isReassignmentOpen = openOperationalReassignmentKey === buildOperationalStepReassignmentKey(assignmentId, step.step_id);
  const companionStep = findOperationalStepCompanion(step, tripRecords);
  const displaySequence = resolveOperationalStepDisplaySequence(step, displaySequenceByStepKey);
  const companionDisplaySequence = resolveOperationalStepDisplaySequence(companionStep, displaySequenceByStepKey);
  const companionClientId = getClientSelectionId(companionStep);
  const orderDetails = buildOperationalStepOrderDetails(step, trip);
  const manualStatus = buildOperationalStepManualStatus(step, reassignmentContexts, suspendDeliveryId, tripRecords);
  const isLoadingOrderProtected = isLoadingStep && isLoadingStepOrderProtected(step, tripRecords);
  const dispatcherActionDeliveryId = resolveManualStatusDeliveryId(step, reassignmentContexts, suspendDeliveryId);
  const dispatcherStatus = buildOperationalStepDispatcherStatus(step, relatedDeliveryItem);
  const sharedLoadingFreezeChain = resolveSharedLoadingFreezeChain(trip, {
    deliveryId,
    orderId: step.order_id,
    stepType: step.step_type,
  });
  const confirmationPortail = (typeof step?.confirmation_portail === "string" && step.confirmation_portail.trim().toLowerCase())
    || (typeof relatedDeliveryItem?.confirmation_portail === "string" && relatedDeliveryItem.confirmation_portail.trim().toLowerCase())
    || "";
  const timeWindowOverride = buildOperationalStepTimeWindowOverride(step, relatedDeliveryItem);
  const isReassignedProtection = manualStatus?.code === "reassigned";
  const isReorderedProtection = manualStatus?.code === "reordered";
  const isOrderSequenceProtected = isReassignedProtection || isReorderedProtection;
  const orderSequenceProtectionMessage = isReassignedProtection
    ? "Commande déjà protégée par réaffectation"
    : (isReorderedProtection ? "Commande déjà protégée par réordonnancement" : "");
  // Tonnage chargé réel saisi (copie dans le Dispatch) : « Libérer » est refusé par le serveur, le bouton est grisé.
  const loadedTonnageReleaseMessage = isLoadingStep && (step.is_confirmed || isLoadingOrderProtected) && step.actual_loaded_quantity_tons != null
    ? "Tonnage chargé saisi dans le Data Manager : effacez-le d'abord pour libérer ce chargement."
    : "";
  const isProtected = manualStatus?.code === "protected";
  const releaseDeliveryId = typeof manualStatus?.deliveryId === "string" ? manualStatus.deliveryId : "";
  const blockingReason = typeof step?.blocking_reason === "string" ? step.blocking_reason.trim() : "";
  const isPendingApprovalBlock = Boolean(step?.is_blocked) && isPendingApprovalBlockingReason(blockingReason);
  const hasHardBlockingReason = Boolean(step?.is_blocked) && !isPendingApprovalBlock;
  const activationHighlightKey = isLoadingStep
    ? getLoadingHighlightKey(step.step_id)
    : getDeliveryHighlightKey(deliveryId);
  const loadingOrderHighlightKey = isLoadingStep
    ? getLoadingOrderHighlightKey(step.order_id)
    : "";
  const dispatcherActionButton = canManageDispatcherFreeze === true
    && dispatcherActionDeliveryId
    && dispatcherStatus?.code !== "delivered"
    && !isImplicitFrozenDispatcherStatusCode(dispatcherStatus?.code)
    ? (() => {
        const isFrozen = dispatcherStatus?.code === "frozen";
        const isPortalFreeze = isFrozen && dispatcherStatus?.gelOrigine === "portail";
        return {
          ariaLabel: isFrozen
            ? (isPortalFreeze
              ? "Dégeler cette commande — la confirmation portail passera à « à revalider »"
              : "Dégeler cette commande")
            : "Geler la commande pour exclure ce groupe de toute réoptimisation",
          deliveryId: dispatcherActionDeliveryId,
          disabled: pendingDeliveryIds.has(dispatcherActionDeliveryId),
          icon: isFrozen ? "rotate-ccw" : "pause",
          label: isFrozen ? (isPortalFreeze ? "Dégeler (portail)" : "Dégeler") : "Geler",
          mode: isFrozen ? "unfreeze" : "freeze",
          gelOrigine: isPortalFreeze ? "portail" : (isFrozen ? "manuel" : null),
          requiresPortalUnfreezeConfirmation: isPortalFreeze,
          title: isFrozen
            ? (isPortalFreeze ? "Dégeler — confirmation portail" : "Dégeler")
            : "Geler",
        };
      })()
    : null;
  const ganttDispatcherActionButton = buildGanttDispatcherActionButton({
    canManageDispatcherFreeze,
    dispatcherActionButton,
    dispatcherStatus,
    freezeChain: sharedLoadingFreezeChain,
    isLoadingStep,
    pendingDeliveryIds,
  });

  return {
    addressLabel: step.address || "Adresse non disponible",
    assignmentId: assignmentId || "",
    clientId,
    companionStep: companionStep
      ? {
          addressLabel: companionStep.address || "Adresse non disponible",
          displaySequence: companionDisplaySequence,
          sequenceLabel: formatOperationalStepSequenceLabel(companionDisplaySequence),
          title: normalizeClientDisplayLabel(companionStep.client_name || companionStep.label)
            || companionStep.order_id
            || (companionStep.step_type === "loading" ? "Chargement" : "Livraison"),
          typeLabel: companionStep.step_type === "loading" ? "Chargement" : "Déchargement",
        }
      : null,
    confirmButton: isLoadingStep
      ? loadingStepId
        ? {
            ariaLabel: isOrderSequenceProtected
              ? orderSequenceProtectionMessage
              : (step.is_confirmed || isLoadingOrderProtected)
              ? "Libérer la commande pour optimisation"
              : "Protéger la commande (chargement et déchargement liés)",
            ariaPressed: (step.is_confirmed || isLoadingOrderProtected) ? "true" : "false",
            disabled: pendingLoadingIds.has(loadingStepId) || isOrderSequenceProtected || Boolean(loadedTonnageReleaseMessage),
            disabledReason: isOrderSequenceProtected ? orderSequenceProtectionMessage : loadedTonnageReleaseMessage,
            icon: (step.is_confirmed || isLoadingOrderProtected) ? "rotate-ccw" : "shield",
            isActive: Boolean(step.is_confirmed || isLoadingOrderProtected),
            isBlocked: false,
            label: (step.is_confirmed || isLoadingOrderProtected) ? "Libérer" : "Protéger",
            targetId: loadingStepId,
            text: "\u2713",
            title: isOrderSequenceProtected
              ? orderSequenceProtectionMessage
              : loadedTonnageReleaseMessage
              || (step.is_confirmed || isLoadingOrderProtected ? "Libérer pour optimisation" : "Protéger la commande"),
            type: "loading",
          }
        : null
      : deliveryId
        ? {
            ariaLabel: isOrderSequenceProtected
              ? orderSequenceProtectionMessage
              : (step.is_confirmed ? "Libérer la commande pour optimisation" : "Protéger la commande"),
            ariaPressed: step.is_confirmed ? "true" : "false",
            disabled: pendingDeliveryIds.has(deliveryId) || hasHardBlockingReason || isOrderSequenceProtected,
            disabledReason: isOrderSequenceProtected
              ? orderSequenceProtectionMessage
              : (hasHardBlockingReason && blockingReason ? blockingReason : ""),
            icon: step.is_confirmed ? "rotate-ccw" : "shield",
            isActive: Boolean(step.is_confirmed),
            isBlocked: hasHardBlockingReason,
            label: step.is_confirmed ? "Libérer" : "Protéger",
            targetId: deliveryId,
            text: "\u2713",
            title: isOrderSequenceProtected
              ? orderSequenceProtectionMessage
              : hasHardBlockingReason && blockingReason
              ? blockingReason
              : (step.is_confirmed ? "Libérer pour optimisation" : "Protéger"),
            type: "delivery",
          }
        : null,
    confirmationPortail: confirmationPortail || null,
    deliveryId,
    dispatcherActionButton,
    ganttDispatcherActionButton,
    sharedLoadingFreezeChain,
    dispatcherStatus,
    displaySequence,
    grainCode: orderDetails?.grainCode || "",
    grainType: typeof (orderDetails?.grainType || step?.grain_type) === "string"
      ? (orderDetails?.grainType || step?.grain_type).trim()
      : "",
    isActivationHighlighted: (
      (Boolean(activationHighlightKey) && transientActivatedStepKeys.has(activationHighlightKey))
      || (Boolean(loadingOrderHighlightKey) && transientActivatedStepKeys.has(loadingOrderHighlightKey))
    ),
    isBlocked: hasHardBlockingReason,
    isConfirmed: Boolean(step.is_confirmed || isLoadingOrderProtected),
    isLinkedSelectionHighlighted: Boolean(companionClientId) && companionClientId === selectedClientId,
    isLoadingStep,
    manualStatus,
    manualMixBlockIndex: Number.isInteger(Number(step?.manual_mix_block_index))
      ? Number(step.manual_mix_block_index)
      : null,
    isSelected: Boolean(clientId) && selectedClientId === clientId,
    key: step.step_id || `${assignmentId || "assignment"}-${step.order_id || "step"}-${step.sequence || 0}`,
    metaLabel: `${step.order_id || "Ordre -"}${step.grain_type ? ` - ${step.grain_type}` : ""} - ${typeof step.quantity_tons === "number" ? `${formatTons(step.quantity_tons)} t` : "Tonnage indisponible"}`,
    quantityLabel: orderDetails?.quantityLabel
      || (typeof step.quantity_tons === "number" ? `${formatTons(step.quantity_tons)} t` : "Tonnage indisponible"),
    orderId: step.order_id || "",
    workOrderNumber: step.work_order_number || orderDetails?.workOrderNumber || "",
    orderDetails,
    reassignButton: hasReassignmentContexts
      ? {
          ariaExpanded: isReassignmentOpen ? "true" : "false",
          ariaLabel: isProtected ? "Retirer la protection avant de réaffecter cette commande" : reassignmentTitle,
          assignmentId: assignmentId || "",
          disabled: Boolean(allReassignmentsPending || isProtected),
          icon: "shuffle",
          isActive: isReassignmentOpen,
          stepId: step.step_id || "",
          text: "\u21c4",
          title: isProtected ? "Retirer la protection avant de réaffecter cette commande" : reassignmentTitle,
        }
      : null,
    releaseButton: (manualStatus?.code === "reassigned" || manualStatus?.code === "reordered") && releaseDeliveryId
      ? {
          ariaLabel: "Libérer la commande pour optimisation",
          deliveryId: releaseDeliveryId,
          disabled: pendingDeliveryIds.has(releaseDeliveryId),
          icon: "rotate-ccw",
          label: "Libérer",
          title: "Libérer pour optimisation",
        }
      : null,
    sequenceLabel: formatOperationalStepSequenceLabel(displaySequence),
    stepId: step.step_id || "",
    stepType: step.step_type || "",
    summaryLabel,
    summaryTitle: step.step_type === "delivery" && hasHardBlockingReason && blockingReason ? blockingReason : "",
    timeWindowOverride,
    suspendButton: suspendDeliveryId
      ? {
          deliveryId: suspendDeliveryId,
          disabled: pendingDeliveryIds.has(suspendDeliveryId) || isProtected,
          icon: "pause",
          text: "\u23f8",
          title: isProtected ? "Retirer la protection avant de suspendre cette commande" : "Suspendre",
          ariaLabel: isProtected ? "Retirer la protection avant de suspendre cette commande" : "Suspendre la commande",
        }
      : deliveryId
        ? {
            deliveryId,
            disabled: pendingDeliveryIds.has(deliveryId) || isProtected,
            icon: "pause",
            text: "\u23f8",
            title: isProtected ? "Retirer la protection avant de suspendre cette commande" : "Suspendre",
            ariaLabel: isProtected ? "Retirer la protection avant de suspendre cette commande" : "Suspendre la commande",
          }
        : null,
    title: normalizeClientDisplayLabel(step.client_name || step.label) || step.order_id || (isLoadingStep ? "Chargement" : "Livraison"),
    tripId: step.trip_id || "",
  };
}

function getTripDistanceSummary(trip, fallbackDistanceSummary) {
  return trip?.route?.estimated_distance_label
    || trip?.total_estimated_distance_label
    || fallbackDistanceSummary
    || "Distance estimée indisponible";
}

function getTripGroupSummaryLabel(trip) {
  const deliveryCount = Number.isFinite(trip?.delivery_count)
    ? trip.delivery_count
    : Array.isArray(trip?.clients)
      ? trip.clients.length
      : 0;
  const loadLabel = Number.isFinite(trip?.peak_load_tons)
    ? `Pic ${formatTons(trip.peak_load_tons)} t`
    : null;
  const deliveryLabel = `${deliveryCount} livraison(s)`;
  return [loadLabel, deliveryLabel].filter(Boolean).join(" - ");
}

export function buildTripGroups({
  assignment,
  assignmentId,
  canManageDispatcherFreeze = false,
  fallbackDistanceSummary,
  openOperationalReassignmentKey,
  operationalStepRecords,
  pendingDeliveryIds,
  pendingLoadingIds,
  selectedClientId,
  selectedTripId,
  transientActivatedStepKeys,
}) {
  const recordsByTripId = new Map();
  operationalStepRecords.forEach((stepRecord) => {
    const tripId = stepRecord?.step?.trip_id || "__ungrouped__";
    const records = recordsByTripId.get(tripId) || [];
    records.push(stepRecord);
    recordsByTripId.set(tripId, records);
  });

  const trips = Array.isArray(assignment?.trips) ? assignment.trips.filter(Boolean) : [];
  const displaySequenceLookupByTripId = new Map(
    trips.map((trip) => [
      trip?.trip_id || "",
      buildOperationalStepDisplaySequenceLookup(trip, recordsByTripId.get(trip?.trip_id) || []),
    ]),
  );
  const groupedTrips = trips
    .map((trip, index) => {
      const tripRecords = recordsByTripId.get(trip.trip_id) || [];
      const displaySequenceByStepKey = displaySequenceLookupByTripId.get(trip?.trip_id || "") || EMPTY_OPERATIONAL_STEP_SEQUENCE_LOOKUP;
      return {
        distanceSummary: getTripDistanceSummary(trip, fallbackDistanceSummary),
        isSelectedTrip: Boolean(selectedTripId) && selectedTripId === trip.trip_id,
        operationalStepCountLabel: `${tripRecords.length} étape(s)`,
        operationalSteps: tripRecords.map((stepRecord) => buildOperationalStepViewModel({
          assignmentId,
          canManageDispatcherFreeze,
          displaySequenceByStepKey,
          openOperationalReassignmentKey,
          pendingDeliveryIds,
          pendingLoadingIds,
          selectedClientId,
          stepRecord,
          transientActivatedStepKeys,
          trip,
          tripRecords,
        })),
        routeBadge: getTripRouteBadgeMeta(trip),
        summaryLabel: getTripGroupSummaryLabel(trip),
        title: "Plan de tournée",
        tripId: trip.trip_id || `trip-${index + 1}`,
      };
    })
    .filter((group) => group.operationalSteps.length > 0);

  if (groupedTrips.length > 0) {
    return groupedTrips;
  }

  return [{
    distanceSummary: fallbackDistanceSummary,
    isSelectedTrip: false,
    operationalStepCountLabel: `${operationalStepRecords.length} étape(s)`,
    operationalSteps: operationalStepRecords.map((stepRecord) => buildOperationalStepViewModel({
      assignmentId,
      canManageDispatcherFreeze,
      displaySequenceByStepKey: displaySequenceLookupByTripId.get(stepRecord?.step?.trip_id || "") || EMPTY_OPERATIONAL_STEP_SEQUENCE_LOOKUP,
      openOperationalReassignmentKey,
      pendingDeliveryIds,
      pendingLoadingIds,
      selectedClientId,
      stepRecord,
      transientActivatedStepKeys,
      trip: null,
      tripRecords: operationalStepRecords,
    })),
    routeBadge: getAssignmentTripCount(assignment) > 0 ? { label: "Route", tone: "neutral" } : null,
    summaryLabel: "Vue agrégée",
    title: "Étapes opérationnelles",
    tripId: `${assignmentId || "assignment"}-operations`,
  }];
}
